/* eslint-disable react/no-unknown-property */
/* eslint-disable no-nested-ternary */
import {
  Action, ActionIcon, Form, FormHandlers,
  FormSubmitType,
  HStack,
  ICON_CLOSE,
  ICON_CURRENCY_EUR,
  ICON_PERCENT,
  Label,
  Stack,
} from '@doveit/bricks';
import React from 'react';
import AgentAutoComplete from '../../../containers/agent-autocomplete/AgentAutoComplete';
import { Agent } from '../../../providers/api/dtos';
import { invoiceCommissionFormValidationSchema } from './InvoiceCommissionForm.schema';
import { InvoiceCommissionValueType } from '../../types';
import { ViewAgentSmallPreview } from '../../../agent/components';
import { commissionTypeLabels, jobTitleLabels } from '../../../labels';
import { removeNullAndUndefinedValues } from '../../../utils/array';
import { asSelectOptions } from '../../../select-options/utils/asSelectOptions';
import * as styles from './InvoiceCommissionForm.style';
import { CommissionType } from '../../../domain/types';

export interface InvoiceCommissionFormModel {
  agentId: Agent['id'];
  type: CommissionType,
  value: string;
  valueType: InvoiceCommissionValueType;
}

export interface InvoiceCommissionFormProps extends React.AriaAttributes {
  initialValues?: Partial<InvoiceCommissionFormModel>,
  agentIdsToExclude?: number[],
  innerRef?: React.MutableRefObject<FormHandlers>,
  loading?: boolean,
  size?: 'M' | 'S',
  onSubmit: FormSubmitType<InvoiceCommissionFormModel>,
  hideLabels?: boolean,
}

export const INVOICE_COMMISSION_FORM_DEFAULT_VALUES: InvoiceCommissionFormModel = {
  agentId: undefined,
  type: CommissionType.COMMISSION,
  value: '',
  valueType: InvoiceCommissionValueType.PERCENTAGE,
};

const InvoiceCommissionForm: React.FC<InvoiceCommissionFormProps> = ({
  initialValues,
  size = 'M',
  agentIdsToExclude = [],
  hideLabels = false,
  ...rest
}) => {
  const mergedInitialValues: InvoiceCommissionFormModel = React.useMemo(() => ({
    ...INVOICE_COMMISSION_FORM_DEFAULT_VALUES,
    ...initialValues,
  }), [initialValues]);

  return (
    <Form
      validationSchema={invoiceCommissionFormValidationSchema}
      initialValues={mergedInitialValues}
      {...rest}
    >
      {({
        values, setFieldValue, errors, dirty,
      }) => {
        const swapCommissionsType = () => {
          setFieldValue('valueType', values.valueType === InvoiceCommissionValueType.FIXED
            ? InvoiceCommissionValueType.PERCENTAGE
            : InvoiceCommissionValueType.FIXED);
          setFieldValue('value', '');
        };

        return (
          <>
            <Form.Item size={{ MD: 3 / 8 }}>
              {values.agentId && (
                <Stack gap={150}>
                  {!hideLabels && (
                    <Label text="Agente" />
                  )}
                  <HStack wrap="nowrap">
                    <div css={styles.agentWrapper}>
                      <ViewAgentSmallPreview
                        agentIdOrEmail={values.agentId!}
                        size={size}
                        details={(agent) => jobTitleLabels[agent.jobTitle]}
                      />
                    </div>
                    <ActionIcon
                      label="Deseleziona agente"
                      size={size}
                      icon={{ path: ICON_CLOSE }}
                      onClick={() => setFieldValue('agentId', undefined)}
                    />
                  </HStack>
                </Stack>
              )}
              {!values.agentId && (
                <AgentAutoComplete
                  size={size}
                  label={!hideLabels ? 'Agente' : undefined}
                  placeholder="Cerca agente"
                  onSuggestionSelected={(agent) => setFieldValue('agentId', agent?.id)}
                  clearValueOnSelection
                  excludeAgentsById={removeNullAndUndefinedValues([...agentIdsToExclude, values.agentId])}
                  required
                  error={errors.agentId && dirty ? errors.agentId : undefined}
                />
              )}
            </Form.Item>
            <Form.Item size={{ MD: 2 / 8 }}>
              <Form.Select
                name="type"
                options={asSelectOptions(commissionTypeLabels)}
                size={size}
                label={!hideLabels ? 'Tipologia' : undefined}
              />
            </Form.Item>
            <Form.Item size={{ MD: 3 / 8 }}>
              <HStack alignItems="end" wrap="nowrap">
                <div style={{ flexGrow: 1 }}>
                  <Form.DecimalPriceField
                    name="value"
                    size={size}
                    label={!hideLabels ? (values.valueType === InvoiceCommissionValueType.FIXED ? 'Importo (€)' : 'Percentuale (%)') : undefined}
                    required
                    icon={values.valueType === InvoiceCommissionValueType.FIXED ? ICON_CURRENCY_EUR : ICON_PERCENT}
                  />
                </div>
                <Action
                  size={size}
                  label={values.valueType === InvoiceCommissionValueType.FIXED ? 'Usa %' : 'Usa €'}
                  onClick={swapCommissionsType}
                />
              </HStack>
            </Form.Item>
          </>
        );
      }}
    </Form>
  );
};

export default InvoiceCommissionForm;
